import isEqual from 'lodash.isequal';
import * as React from 'react';

import { requestSuggestions } from 'src/actions/data-sources/action-creators/requestSuggestions';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  MappingSuggestion,
  ProjectResourceSuggestionsData,
} from 'src/containers/ProjectResourceSuggestionsDialog/projectResourceSuggestionsData';
import { getMappingSuggestions } from 'src/containers/ProjectResourceSuggestionsDialog/utils/getMappingSuggestions';
import { IgnoredSuggestion } from 'src/containers/ProjectResourceSuggestionsDialog/utils/getOrganizedSuggestionRecords';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectMappingsSelector } from 'src/selectors/projectMappings';
import { coercePortalConnection } from 'src/store/connections/types';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { usePortalConnectionsWithDemo } from 'src/utils/hooks/usePortalConnectionsWithDemo';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useFetchMappingSuggestions = () => {
  const dispatch = useReduxDispatch();
  const diagram = useDiagram();
  const defaultPortalConnection = useDefaultPortalConnection();
  const portalConnections = usePortalConnectionsWithDemo();

  const { projectId } = useRouterParams();

  const labelsByIri = useReduxSelector(
    projectLabelsByIriSelector(projectId),
    isEqual
  );
  const mappings = useReduxSelector(
    projectMappingsSelector(projectId),
    isEqual
  );

  const isMounted = useIsMounted();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const fetchMappingSuggestions = React.useCallback(
    async ({
      selectedClasses,
      selectedResources,
    }: Pick<
      ProjectResourceSuggestionsData,
      'selectedClasses' | 'selectedResources'
    >): Promise<{
      ignoredSuggestions: IgnoredSuggestion[] | null;
      mappingSuggestions: MappingSuggestion[] | null;
    }> => {
      if (!defaultPortalConnection || !diagram) {
        return { ignoredSuggestions: null, mappingSuggestions: null };
      }

      // Request mapping suggestions
      const connections = portalConnections?.map(coercePortalConnection);

      setIsLoading(true);
      const suggestions = await dispatch(
        requestSuggestions({
          classIris: selectedClasses,
          connections,
          defaultConnection: defaultPortalConnection,
          projectId,
          resourceIds: selectedResources,
        })
      );

      setIsLoading(false);
      if (!isMounted.current) {
        return { ignoredSuggestions: null, mappingSuggestions: null };
      }

      if (!suggestions) {
        return { ignoredSuggestions: [], mappingSuggestions: null };
      }

      const { ignoredSuggestions, mappingSuggestions } = getMappingSuggestions({
        labelsByIri,
        mappings,
        selectedClasses,
        suggestions,
      });
      if (!mappingSuggestions.length) {
        return { ignoredSuggestions, mappingSuggestions: null };
      }

      return { ignoredSuggestions: null, mappingSuggestions };
    },
    [
      defaultPortalConnection,
      diagram,
      dispatch,
      isMounted,
      labelsByIri,
      mappings,
      portalConnections,
      projectId,
    ]
  );

  return {
    fetchMappingSuggestions,
    isLoading,
    setIsLoading,
  };
};
